import type { Ref } from 'vue'
import type { AppTextareaEvent, NativeSelectMode, NativeSelectOption } from '@shared/ui/fields'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { useDayjs } from '@shared/lib/format'
import { useToast } from '@shared/ui/toast'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineTasksStore } from './tasks/store'
import { useTutorTaskPostponeDatesApi } from '../api/tutor-tasks/postpone-dates.hook'
import { useTutorTaskPostponeApi } from '../api/tutor-tasks/postpone.hook'
import { useDisciplineTasksRefresh } from './discipline-tasks-refresh.hook'
import { isReasonTaskType } from './tutor-tasks/tutor-tasks'

interface ModalDisciplineTaskPostponeReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refDateFrom: Ref<string>
  refDateTo: Ref<string>
  refHasReason: ComputedRef<boolean>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: Ref<string | null>
  refReasonMode: Ref<NativeSelectMode>
  refComment: Ref<string>
  setReason: (option: NativeSelectOption) => void
  setComment: (event: AppTextareaEvent) => void
  closeModal: () => void
  postponeTask: () => Promise<void>
}

const DATE_FORMAT = DateFormat.FileDate
const NOT_SELECTED_TEXT = '-- Not selected --'

export const useModalDisciplineTaskPostpone = (): ModalDisciplineTaskPostponeReturn => {
  const toast = useToast()
  const dayjs = useDayjs()
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const { writeAndRefresh } = useDisciplineTasksRefresh()

  const refIsLoading = ref(false)
  const refDateFrom = ref('')
  const refDateTo = ref('')
  const refReason = ref<string | null>(null)
  const refReasonMode = ref<NativeSelectMode>('normal')
  const refComment = ref('')

  const refTask = computed(() => store.getActiveTask)
  const refIsOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.Postpone)
  const refHasReason = computed<boolean>(() => !!refTask.value && isReasonTaskType(refTask.value.type))

  const refReasonOptions = computed<NativeSelectOption[]>(() => {
    const type = refTask.value?.type
    const reasons = (type && listsStore.getLists?.tutor_task.reason[type]) || []

    return [
      { id: 0, value: null, text: NOT_SELECTED_TEXT, selected: refReason.value === null },
      ...reasons.map(reason => ({ id: reason, value: reason, text: reason, selected: reason === refReason.value })),
    ]
  })

  const setReason = (option: NativeSelectOption): void => {
    refReason.value = option.value === null ? null : String(option.value)
    refReasonMode.value = refReason.value ? 'normal' : 'error'
  }

  const setComment = (event: AppTextareaEvent): void => {
    refComment.value = event.value
  }

  const closeModal = (): void => {
    if (refIsLoading.value) return
    store.closeModal()
  }

  /** No confirmed lesson follows: the declined popup replaces the modal, as the API asks. */
  const loadDates = async (): Promise<void> => {
    const task = refTask.value
    if (!task) return

    const dates = await useTutorTaskPostponeDatesApi({
      taskId: task.id,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!dates) {
      store.closeModal()
      return
    }

    if (!dates.to) {
      store.openModal(TutorTaskModal.PostponeDeclined, task)
      return
    }

    refDateFrom.value = dates.from ? dayjs(dates.from).format(DATE_FORMAT) : ''
    refDateTo.value = dayjs(dates.to).format(DATE_FORMAT)
  }

  /** An empty comment is not sent: the legacy modal sent '' and wiped the tutor's own comment. */
  const postponeTask = async (): Promise<void> => {
    const task = refTask.value
    if (!task || refIsLoading.value) return

    if (refHasReason.value && !refReason.value) {
      refReasonMode.value = 'error'
      toast.warning('Fields are required.')
      return
    }

    const comment = refComment.value.trim()

    await writeAndRefresh(refIsLoading, () => useTutorTaskPostponeApi({
      taskId: task.id,
      payload: {
        ...(comment && { comment }),
        ...(refHasReason.value && { reason: refReason.value }),
      },
    }))
  }

  watch(refIsOpen, (isOpen) => {
    if (!isOpen) return

    refDateFrom.value = ''
    refDateTo.value = ''
    refReason.value = refTask.value?.reason ?? null
    refReasonMode.value = 'normal'
    refComment.value = ''
    void loadDates()
  })

  return {
    refIsOpen,
    refIsLoading,
    refDateFrom,
    refDateTo,
    refHasReason,
    refReasonOptions,
    refReason,
    refReasonMode,
    refComment,
    setReason,
    setComment,
    closeModal,
    postponeTask,
  }
}
